/**
 * eqla-pack-selector.js — Sélecteur NATIF de packs (EXPÉRIENCE sandbox).
 *
 * Adapté de product-pack-subscription-pricing.js : même logique de synchro
 * prix <-> Appstle (achat unique / abonnement), mais cible les cartes NATIVES
 * `.eqla-pack-card` au lieu des `.moonbundle-option`.
 *
 * Responsabilités :
 *  - clic sur une carte -> sélectionne la variante (tous les input[name="id"])
 *    + son selling_plan si l'abonnement est actif, refresh prix Shopify section.
 *  - met à jour prix / prix barré / « X€/mois » / « Économisez X€ » sur chaque
 *    carte selon l'état achat unique vs abonnement.
 */
(function () {
  const APPSTLE_EVENTS = [
    'AppstleSubscription:SubscriptionWidget:widgetInitialised',
    'AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated',
    'AppstleSubscription:SubscriptionWidget:SellingPlanSelected',
    'AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected',
    'AppstleSubscription:SubscriptionWidget:sellingPlanChanged',
  ];

  const VARIANT_DURATION_RULES = [
    { variantMatch: /6\s*mois/i, planKeywords: ['semestriel', 'semiannual', '6 month', 'every 6'] },
    { variantMatch: /3\s*mois/i, planKeywords: ['trimestriel', 'quarterly', '3 month', 'every 3'] },
    { variantMatch: /1\s*mois/i, planKeywords: ['mensuel', 'monthly', '1 month', 'every month', 'every 1'] },
  ];

  function formatMoney(cents) {
    var out;
    if (window.theme && window.theme.Currency && window.shopSettings) {
      out = theme.Currency.formatMoney(cents, window.shopSettings.moneyFormat);
    } else {
      out = (cents / 100).toFixed(2).replace('.', ',') + '€';
    }
    // nowmade 2026-09-08 : pas de « .00 » (115.00€ -> 115€ ; 94.50€ reste 94.50€)
    return String(out).replace(/([.,])00(?!\d)/, '');
  }

  function findSellingPlan(sellingPlanGroups, planId) {
    if (!sellingPlanGroups || !planId) return null;
    for (const group of sellingPlanGroups) {
      const plans = group.selling_plans || [];
      const match = plans.find((plan) => String(plan.id) === String(planId));
      if (match) return match;
    }
    return null;
  }

  function applyPriceAdjustments(basePriceCents, adjustments) {
    if (!adjustments || !adjustments.length) return basePriceCents;
    let price = basePriceCents;
    const sorted = [...adjustments].sort((a, b) => (a.position || 0) - (b.position || 0));
    sorted.forEach((adjustment) => {
      if (adjustment.value_type === 'percentage') {
        price = Math.round((price * (100 - adjustment.value)) / 100);
      } else if (adjustment.value_type === 'fixed_amount') {
        price -= adjustment.value;
      } else if (adjustment.value_type === 'price') {
        price = adjustment.value;
      }
    });
    return price;
  }

  function getSellingPlanInput(section) {
    return (
      section.querySelector('form[action*="/cart/add"] input[name="selling_plan"]') ||
      section.querySelector('input[name="selling_plan"]')
    );
  }

  function isOneTimeAppstleRadio(radio) {
    if (!radio) return false;
    return (
      !radio.value ||
      radio.value === '0' ||
      !!radio.closest(
        '[class*="one_time"], [class*="onetime"], [class*="one-time"], [class*="purchase_option_one"]'
      )
    );
  }

  function getSellingPlanId(section) {
    const input = getSellingPlanInput(section);
    if (input && input.value) return input.value;

    const selectedPlan = section.querySelector('[class*="appstle"] select[name="selling_plan"]');
    if (selectedPlan && selectedPlan.value) return selectedPlan.value;

    const checkedRadio = section.querySelector('[class*="appstle"] input[type="radio"]:checked');
    if (checkedRadio && !isOneTimeAppstleRadio(checkedRadio) && checkedRadio.value) {
      return checkedRadio.value;
    }
    return null;
  }

  function isSubscriptionActive(section) {
    // La radio Appstle cochée fait FOI quand elle existe (le hidden input
    // selling_plan peut rester "périmé" un court instant après un toggle).
    const checkedRadio = section.querySelector('[class*="appstle"] input[type="radio"]:checked');
    if (checkedRadio) {
      return !isOneTimeAppstleRadio(checkedRadio);
    }
    // Pas de widget radio : on retombe sur le selling_plan input/select.
    return !!getSellingPlanId(section);
  }

  function matchPlanIdToVariant(variant) {
    const title = variant.title || '';
    const allocations = variant.selling_plan_allocations || [];
    for (const rule of VARIANT_DURATION_RULES) {
      if (!rule.variantMatch.test(title)) continue;
      const allocationMatch = allocations.find((allocation) => {
        const planName = (allocation.selling_plan_name || '').toLowerCase();
        return rule.planKeywords.some((keyword) => planName.includes(keyword));
      });
      if (allocationMatch) return allocationMatch.selling_plan_id;
    }
    return allocations[0]?.selling_plan_id || null;
  }

  function getSellingPlanIdForVariant(variant, section) {
    const allocations = variant.selling_plan_allocations || [];
    const durationPlanId = matchPlanIdToVariant(variant);
    if (durationPlanId) return durationPlanId;

    const activePlanId = getSellingPlanId(section);
    if (activePlanId) {
      const hasActiveAllocation = allocations.some(
        (allocation) => String(allocation.selling_plan_id) === String(activePlanId)
      );
      if (hasActiveAllocation) return activePlanId;
    }
    if (allocations.length === 1) return allocations[0].selling_plan_id;
    return activePlanId || allocations[0]?.selling_plan_id || null;
  }

  function getSubscriptionPriceForVariant(variant, section, sellingPlanGroups) {
    const oneTimePrice = variant.price;
    const sellingPlanId = getSellingPlanIdForVariant(variant, section);
    if (!sellingPlanId) return null;

    const allocations = variant.selling_plan_allocations || [];
    const allocation = allocations.find(
      (entry) => String(entry.selling_plan_id) === String(sellingPlanId)
    );
    if (allocation) return allocation.price;

    // nowmade: aucune allocation reelle sur cette variante -> pas de prix abo
    // (evite d'appliquer une fausse remise au 1 mois qui n'a pas d'abonnement)
    return null;
  }

  /* ── Sélection de variante (forms) ─────────────────────────────────── */
  function updateAllVariantInputs(section, sectionId, variantId) {
    const selectors = [
      `#product-form-${sectionId} input[name="id"]`,
      `#product-form-installment-${sectionId} input[name="id"]`,
      `#product-form-${sectionId}--alt input[name="id"]`,
    ];
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((input) => {
        if (input.value === String(variantId)) return;
        input.value = variantId;
        input.removeAttribute('disabled');
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    section.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
      if (input.value === String(variantId)) return;
      input.value = variantId;
      input.removeAttribute('disabled');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // Surcharge le bloc prix (ATC + sticky) avec le prix d'abonnement calculé,
  // car le rendu de section Shopify n'intègre pas le selling_plan.
  function applySubscriptionPriceOverride(section, sectionId, variant, data) {
    if (!isSubscriptionActive(section)) return;
    const subPrice = getSubscriptionPriceForVariant(variant, section, data.sellingPlanGroups);
    if (subPrice == null) return;
    [`price-${sectionId}`, `price-${sectionId}--alt`].forEach((priceId) => {
      const block = document.getElementById(priceId);
      if (!block) return;
      const priceEl =
        block.querySelector('.price-item--sale, .price-item--regular, .price-item') || block;
      if (priceEl) priceEl.textContent = formatMoney(subPrice);
    });
  }

  function updateSectionPrices(section, sectionId, variantId, variant, data) {
    const productPath = window.location.pathname;
    const url = `${productPath}?variant=${variantId}&section_id=${sectionId}`;
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        [`price-${sectionId}`, `price-${sectionId}--alt`].forEach((priceId) => {
          const destination = document.getElementById(priceId);
          const source = doc.getElementById(priceId);
          if (destination && source) destination.innerHTML = source.innerHTML;
        });
        // En abonnement, on surcharge avec le prix abo (le rendu Shopify ne le connaît pas).
        if (variant && data) applySubscriptionPriceOverride(section, sectionId, variant, data);
        paintAtcButtonPrice(section, sectionId);
      })
      .catch(() => {});
  }

  function syncSellingPlanForVariant(section, variant) {
    if (!isSubscriptionActive(section)) return;
    const planId = matchPlanIdToVariant(variant);
    if (!planId) return;
    const hasAllocation = (variant.selling_plan_allocations || []).some(
      (allocation) => String(allocation.selling_plan_id) === String(planId)
    );
    if (!hasAllocation) return;

    const input = getSellingPlanInput(section);
    if (input && input.value !== String(planId)) {
      input.value = planId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const select = section.querySelector('[class*="appstle"] select[name="selling_plan"]');
    if (select && select.value !== String(planId)) {
      const option = select.querySelector(`option[value="${planId}"]`);
      if (option) {
        select.value = planId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function selectVariant(section, sectionId, variant, variantMap, data) {
    const variantId = String(variant.id);

    // état visuel des cartes
    section.querySelectorAll('[data-eqla-pack-card]').forEach((card) => {
      const isThis = card.dataset.variantId === variantId;
      card.classList.toggle('is-selected', isThis);
      const radio = card.querySelector('.eqla-pack-card__radio');
      if (radio) radio.checked = isThis;
    });

    section.dataset.eqlaSelectedVariantId = variantId;

    // nowmade: synchronise la propriété panier _cure_duration avec la variante choisie
    (function () {
      var card = section.querySelector('[data-eqla-pack-card][data-variant-id="' + variantId + '"]');
      var ct = card ? card.dataset.curetime : '';
      if (ct) {
        document.querySelectorAll('input[data-cure-duration-input]').forEach(function (inp) {
          if (inp.value !== ct) inp.value = ct;
        });
      }
    })();

    updateAllVariantInputs(section, sectionId, variantId);

    if (isSubscriptionActive(section)) {
      syncSellingPlanForVariant(section, variant);
    }

    updateSectionPrices(section, sectionId, variantId, variant, data);
  }

  /* ── Mise à jour des prix sur les cartes ───────────────────────────── */
  // Fréquence affichée dans le sticky : pack sélectionné + état abonnement.
  function updateStickyFreq(section) {
    const bar = document.getElementById('stickyAtcBar');
    if (!bar) return;
    const left = bar.querySelector('.sticky-atc-left');
    if (!left) return;
    let freq = bar.querySelector('.sticky-atc-freq');
    if (!freq) {
      freq = document.createElement('span');
      freq.className = 'sticky-atc-freq';
      const title = left.querySelector('.sticky-atc-title');
      if (title && title.parentNode === left) left.insertBefore(freq, title.nextSibling);
      else left.insertBefore(freq, left.firstChild);
    }
    const selectedId = section.dataset.eqlaSelectedVariantId;
    const card =
      (selectedId && section.querySelector('[data-eqla-pack-card][data-variant-id="' + selectedId + '"]')) ||
      section.querySelector('[data-eqla-pack-card].is-selected');
    const titleEl = card ? card.querySelector('.eqla-pack-card__title, [data-eqla-title]') : null;
    let txt = titleEl ? titleEl.textContent.trim() : '';
    if (isSubscriptionActive(section)) txt = (txt ? txt + ' · ' : '') + 'Abonnement';
    if (freq.textContent !== txt) freq.textContent = txt;
  }

  // Widget Alma (paiement 3x/4x) - re-rend avec le prix du pack selectionne (idempotent).
  var __eqlaAlmaAmount = null;
  var __eqlaAlmaRenderedAmount = null;
  var __eqlaAlmaTimer = null;
  var __eqlaAlmaWidgets = null;

  function renderAlmaWidget() {
    var amountCents = __eqlaAlmaAmount;
    var box = document.getElementById('eqla-alma-widget');
    __eqlaAlmaTimer = null;
    if (!window.Alma || !window.Alma.Widgets || !box || !amountCents) return;
    try {
      if (!__eqlaAlmaWidgets) {
        __eqlaAlmaWidgets = window.Alma.Widgets.initialize(
          'merchant_11wVcOe2LvzSkmjnXQAv5crRRZNBE3q1Y0',
          window.Alma.ApiMode.LIVE
        );
      }
      __eqlaAlmaWidgets.add(window.Alma.Widgets.PaymentPlans, {
        container: '#eqla-alma-widget',
        purchaseAmount: amountCents,
        locale: 'fr',
        hideIfNotEligible: true
      });
      __eqlaAlmaRenderedAmount = amountCents;
    } catch (e) {
      __eqlaAlmaRenderedAmount = null;
    }
  }

  // Debounce les mises à jour successives du sélecteur de packs afin qu'une
  // seule instance Alma soit montée avec le dernier montant sélectionné.
  function updateAlmaWidget(amountCents) {
    if (!window.Alma || !window.Alma.Widgets) return;
    var box = document.getElementById('eqla-alma-widget');
    if (!box || !amountCents) return;
    if (
      amountCents === __eqlaAlmaAmount &&
      (__eqlaAlmaTimer || amountCents === __eqlaAlmaRenderedAmount)
    ) {
      return;
    }
    __eqlaAlmaAmount = amountCents;
    if (__eqlaAlmaTimer) clearTimeout(__eqlaAlmaTimer);
    __eqlaAlmaRenderedAmount = null;
    __eqlaAlmaTimer = setTimeout(renderAlmaWidget, 200);
  }

  function paintAtcButtonPrice(section, sectionId) {
    if (!sectionId) return;
    const selectedId = section.dataset.eqlaSelectedVariantId;
    const card =
      (selectedId && section.querySelector('[data-eqla-pack-card][data-variant-id="' + selectedId + '"]')) ||
      section.querySelector('[data-eqla-pack-card].is-selected');
    if (!card) return;
    const nowEl = card.querySelector('[data-eqla-price]');
    const cmpEl = card.querySelector('[data-eqla-compare]');
    const nowText = nowEl ? nowEl.textContent.trim() : '';
    const cmpText = cmpEl && !cmpEl.hidden ? cmpEl.textContent.trim() : '';
    if (!nowText) return;
    const html =
      '<span class="eqla-atc-price">' +
      (cmpText ? '<s class="eqla-atc-price__was">' + cmpText + '</s>' : '') +
      '<span class="eqla-atc-price__now">' + nowText + '</span>' +
      '</span>';
    [`price-${sectionId}`, `price-${sectionId}--alt`].forEach((priceId) => {
      const block = document.getElementById(priceId);
      if (!block) return;
      const curNow = block.querySelector('.eqla-atc-price__now');
      const curWas = block.querySelector('.eqla-atc-price__was');
      const sameNow = curNow && curNow.textContent.trim() === nowText;
      const sameWas = (curWas ? curWas.textContent.trim() : '') === cmpText;
      if (sameNow && sameWas) return;
      block.innerHTML = html;
    });
  }

  function updateCardPrices(section, data, variantMap) {
    const subscriptionActive = isSubscriptionActive(section);

    section.querySelectorAll('[data-eqla-pack-card]').forEach((card) => {
      const variant = variantMap[card.dataset.variantId];
      if (!variant) return;

      const months = parseInt(card.dataset.months, 10) || 1;
      const oneTimePrice = variant.price;
      const compareAtPrice = variant.compare_at_price;

      let displayPrice = oneTimePrice;
      let comparePrice = compareAtPrice;
      let savingsCents = 0;

      // nowmade 2026-09-18 : le 1 mois n'est JAMAIS abonnable en front (le plan « ajout à l'unité »
      // n'existe que pour le portail client) -> pas de prix abo sur la carte 1 mois.
      if (subscriptionActive && months > 1) {
        const subPrice = getSubscriptionPriceForVariant(variant, section, data.sellingPlanGroups);
        if (subPrice != null && subPrice < oneTimePrice) {
          displayPrice = subPrice;
          // nowmade 2026-09-18 : en abo, la référence est le prix SANS réduction pack
          // (compare_at) -> barré 105 € et « Économisez » = 105 - 89,25 (retour Marc).
          comparePrice = (compareAtPrice && compareAtPrice > subPrice) ? compareAtPrice : oneTimePrice;
          savingsCents = comparePrice - subPrice;
        }
      } else if (compareAtPrice && compareAtPrice > oneTimePrice) {
        savingsCents = compareAtPrice - oneTimePrice;
      }

      const priceEl = card.querySelector('[data-eqla-price]');
      const compareEl = card.querySelector('[data-eqla-compare]');
      const perMonthEl = card.querySelector('[data-eqla-permonth]');
      const saveEl = card.querySelector('[data-eqla-save]');

      if (priceEl) priceEl.textContent = formatMoney(displayPrice);

      if (compareEl) {
        if (comparePrice && comparePrice > displayPrice) {
          compareEl.textContent = formatMoney(comparePrice);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (perMonthEl) {
        if (months > 1) {
          const perMonthCents = Math.round(displayPrice / months);
          perMonthEl.textContent = `${formatMoney(perMonthCents)}/mois`;
          perMonthEl.hidden = false;
        } else {
          perMonthEl.hidden = true;
        }
      }

      if (saveEl) {
        if (savingsCents > 0) {
          const label = data.saveLabel || 'Économisez';
          saveEl.textContent = `${label} ${formatMoney(savingsCents)}`;
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
    });

    (function(){
      var _selId = section.dataset.eqlaSelectedVariantId;
      var _selVar = _selId ? variantMap[_selId] : null;
      if (_selVar) {
        var _amt = _selVar.price;
        var _selMonths = parseInt((section.querySelector('[data-eqla-pack-card][data-variant-id="' + _selId + '"]') || {dataset:{}}).dataset.months, 10) || 1;
        if (subscriptionActive && _selMonths > 1) {
          var _sp = getSubscriptionPriceForVariant(_selVar, section, data.sellingPlanGroups);
          if (_sp != null) _amt = _sp;
        }
        updateAlmaWidget(_amt);
      }
    })();
    paintAtcButtonPrice(section, data.sectionId || section.dataset.sectionId);
    updateStickyFreq(section);
  }

  /* ── Init ──────────────────────────────────────────────────────────── */
  function initPackSelector(selector) {
    const section =
      selector.closest('[data-pack-pricing-section]') ||
      document.querySelector('[data-pack-pricing-section]') ||
      selector.closest('section') ||
      document;

    const dataEl = section.querySelector('[data-pack-pricing-data]');
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('[eqla-pack] Unable to parse pack pricing data', error);
      return;
    }

    const variantMap = {};
    (data.variants || []).forEach((variant) => {
      variantMap[String(variant.id)] = variant;
    });

    const sectionId = data.sectionId || section.dataset.sectionId;

    let updateTimer = null;
    function scheduleUpdate(delay) {
      clearTimeout(updateTimer);
      updateTimer = setTimeout(() => updateCardPrices(section, data, variantMap), delay || 0);
    }

    // clic sur une carte -> sélection
    selector.addEventListener('click', (event) => {
      const card = event.target.closest('[data-eqla-pack-card]');
      if (!card) return;
      const variant = variantMap[card.dataset.variantId];
      if (!variant) return;
      // on laisse le radio se cocher, puis on synchronise
      setTimeout(() => {
        selectVariant(section, sectionId, variant, variantMap, data);
        scheduleUpdate(0);
        scheduleUpdate(150);
        scheduleUpdate(400);
      }, 0);
    });

    // clavier (changement de radio)
    selector.addEventListener('change', (event) => {
      if (!event.target.matches('.eqla-pack-card__radio')) return;
      const variant = variantMap[event.target.value];
      if (!variant) return;
      selectVariant(section, sectionId, variant, variantMap, data);
      scheduleUpdate(0);
    });

    // Appstle réagit de façon ASYNCHRONE : quand l'utilisateur bascule achat
    // unique <-> abonnement, le widget met à jour input[name="selling_plan"] et
    // sa radio cochée APRÈS le clic. Les events custom Appstle ne sont pas tous
    // émis selon le type de widget. On combine donc 3 stratégies :
    //   (a) events custom Appstle (quand ils existent),
    //   (b) écoute large change/click avec updates différés (laisse Appstle se poser),
    //   (c) un watcher qui détecte un vrai changement d'état d'abonnement et
    //       re-rend les cartes (robuste à tout type de widget).

    // signature de l'état d'abonnement courant (radio cochée + selling_plan)
    function subscriptionSignature() {
      const sp = getSellingPlanInput(section);
      const spVal = sp && sp.value ? sp.value : '';
      const checked = section.querySelector('[class*="appstle"] input[type="radio"]:checked');
      const checkedId = checked ? checked.id : '';
      const oneTime = checked ? isOneTimeAppstleRadio(checked) : true;
      return `${spVal}|${checkedId}|${oneTime ? 1 : 0}`;
    }

    let lastSignature = subscriptionSignature();

    // Réconcilie le prix affiché sur le bouton ATC + sticky avec l'état courant
    // (achat unique vs abonnement) pour la variante sélectionnée. Léger, sans fetch,
    // idempotent -> peut tourner à chaque tick du watcher pour s'auto-corriger même
    // si Appstle re-render le bloc prix de façon asynchrone.
    function reconcileAtcPrice() {
      paintAtcButtonPrice(section, sectionId);
    }

    function reactToSubscriptionChange() {
      const selectedId = section.dataset.eqlaSelectedVariantId;
      const variant = selectedId ? variantMap[selectedId] : null;
      const subActive = isSubscriptionActive(section);

      if (variant && subActive) {
        syncSellingPlanForVariant(section, variant);
      } else if (!subActive) {
        // achat unique : on purge le selling_plan périmé qu'Appstle aurait laissé,
        // sinon le panier partirait en abonnement par erreur.
        const sp = getSellingPlanInput(section);
        if (sp && sp.value) {
          sp.value = '';
          sp.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateCardPrices(section, data, variantMap);
      // Bascule abo->unique : on refait un rendu Shopify propre pour le bloc prix.
      if (variant && !subActive) {
        updateSectionPrices(section, sectionId, String(variant.id), variant, data);
      }
      reconcileAtcPrice();
      lastSignature = subscriptionSignature();
    }

    function maybeReact() {
      const sig = subscriptionSignature();
      if (sig !== lastSignature) {
        reactToSubscriptionChange();
      } else {
        // état stable : on s'assure juste que le prix ATC reste cohérent en abo
        reconcileAtcPrice();
      }
    }

    // burst d'updates différés : Appstle pose son état sur ~1s après le clic
    function scheduleAppstleSync() {
      [60, 180, 360, 600, 900].forEach((d) => setTimeout(maybeReact, d));
    }

    // (a) events custom Appstle
    APPSTLE_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, () => scheduleAppstleSync());
    });

    // (b) écoute large change/click sur le widget Appstle
    section.addEventListener('change', (event) => {
      const target = event.target;
      if (
        target.matches('input[name="selling_plan"], select[name="selling_plan"]') ||
        target.closest('[class*="appstle"]')
      ) {
        scheduleAppstleSync();
      }
    });

    section.addEventListener('click', (event) => {
      if (event.target.closest('[class*="appstle"]')) {
        scheduleAppstleSync();
      }
    });

    // (c) watcher de secours : sonde l'état d'abonnement 2x/s.
    // Léger (lecture DOM simple) et ne re-rend QUE si la signature change.
    setInterval(maybeReact, 500);

    // Observer pour le widget Appstle injecté tardivement
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList') return;
        shouldUpdate =
          shouldUpdate ||
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.('[class*="appstle"]') || node.querySelector?.('[class*="appstle"]'))
          );
      });
      if (shouldUpdate) scheduleUpdate(50);
    });
    observer.observe(section, { childList: true, subtree: true });

    // État initial : respecte une variante explicitement demandée dans l'URL.
    // Sinon, présélectionne le deuxième pack (3 mois), avec repli sur le
    // premier pack disponible si cette variante n'est pas achetable.
    const cards = Array.from(selector.querySelectorAll('[data-eqla-pack-card]'));
    const requestedVariantId = new URLSearchParams(window.location.search).get('variant');
    const requestedCard = requestedVariantId
      ? selector.querySelector(
          '[data-eqla-pack-card][data-variant-id="' + requestedVariantId + '"]'
        )
      : null;
    const preferredCard = cards[1];
    const preferredVariant = preferredCard
      ? variantMap[preferredCard.dataset.variantId]
      : null;
    const fallbackCard =
      cards.find((card) => variantMap[card.dataset.variantId]?.available !== false) ||
      cards[0];
    const initialCard =
      requestedCard ||
      (preferredVariant?.available !== false ? preferredCard : fallbackCard);

    if (initialCard) {
      const v = variantMap[initialCard.dataset.variantId];
      if (v) {
        selectVariant(section, sectionId, v, variantMap, data);
        setTimeout(function () { selectVariant(section, sectionId, v, variantMap, data); }, 250);
      }
    }

    scheduleUpdate(0);
  }

  function init() {
    document.querySelectorAll('[data-eqla-pack-selector]').forEach(initPackSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* nowmade: galerie photos produit — sync clic miniature <-> scroll horizontal (desktop swipe) */
(function () {
  function initGallery() {
    var list = document.querySelector('.product__media-list');
    if (!list) return;
    var items = [].slice.call(list.querySelectorAll('.product__media-item'));
    var thumbs = [].slice.call(document.querySelectorAll('.thumbnail-list .thumbnail'));
    if (items.length < 2) return;

    function scrollToIndex(i) {
      var target = items[i];
      if (!target) return;
      var delta = target.getBoundingClientRect().left - list.getBoundingClientRect().left;
      list.scrollBy({ left: delta, behavior: 'smooth' });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { scrollToIndex(i); });
    });

    var tick = false;
    list.addEventListener('scroll', function () {
      if (tick) return;
      tick = true;
      requestAnimationFrame(function () {
        tick = false;
        var center = list.getBoundingClientRect().left + list.clientWidth / 2;
        var best = Infinity, activeIdx = 0;
        items.forEach(function (it, i) {
          var r = it.getBoundingClientRect();
          var mid = r.left + r.width / 2;
          var d = Math.abs(mid - center);
          if (d < best) { best = d; activeIdx = i; }
        });
        thumbs.forEach(function (t, i) {
          if (i === activeIdx) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      });
    }, { passive: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGallery);
  else initGallery();
})();


/* nowmade: date d'expédition dynamique (J+4) — remplace la date figée */
(function () {
  function setDeliveryDate() {
    var el = document.querySelector('.delivery-text strong');
    if (!el) return;
    var d = new Date();
    d.setDate(d.getDate() + 4);
    var txt = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    el.textContent = txt;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setDeliveryDate);
  else setDeliveryDate();
})();
